import React, { useState } from 'react';
import { 
  Mail, 
  MessageSquare, 
  Clock,
  Send,
  Building
} from 'lucide-react';
import { toast } from 'react-toastify';
import Header from './shared/Header';
import Footer from './shared/Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Create the email body with the form data
    const mailtoLink = `mailto:support@databackfill.com?subject=Contact Form Submission from ${formData.name}&body=
Name: ${formData.name}%0D%0A
Email: ${formData.email}%0D%0A
Company: ${formData.company}%0D%0A
Message: ${formData.message}`;

    // Open the user's default email client
    window.location.href = mailtoLink;
    
    // Show success toast
    toast.success('Message sent successfully! We will get back to you soon.', {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
    
    // Clear the form
    setFormData({
      name: '',
      email: '',
      company: '',
      message: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 md:pt-32 pb-12 md:pb-24 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-white"></div>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block px-4 py-1 bg-blue-50 rounded-full text-blue-600 font-medium text-sm mb-4">
              Let's Connect
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
              Get in touch with our team
            </h1>
            <p className="text-lg md:text-xl text-gray-600 px-4 sm:px-0">
              Have questions about DataBackfill? We're here to help you sync your GA4 data seamlessly.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
<div className="space-y-8">
  <div>
    <h2 className="text-2xl font-bold mb-6">Contact Information</h2>
    <div className="space-y-6">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
          <Building className="text-blue-600" />
        </div>
        <div>
          <h3 className="font-semibold mb-1">Company Information</h3>
          <p className="text-gray-600">DataBackfill LLC</p>
          <p className="text-gray-600">California, United States</p>
        </div>
      </div>

      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
          <Mail className="text-blue-600" />
        </div>
        <div>
          <h3 className="font-semibold mb-1">Email Us</h3>
          <a 
            href="mailto:support@databackfill.com" 
            className="text-blue-600 hover:text-blue-700"
          >
            support@databackfill.com
          </a>
        </div>
      </div>

      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center flex-shrink-0">
          <Clock className="text-purple-600" />
        </div>
        <div>
          <h3 className="font-semibold mb-1">Business Hours</h3>
          <p className="text-gray-600">We follow the sun</p>
        </div>
      </div>

      <div className="flex items-center space-x-4">
        <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
          <MessageSquare className="text-green-600" />
        </div>
        <div className="flex items-center">
          <a 
            href="https://backfillingcrew.slack.com/archives/C0814RCS3LH"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-blue-600 hover:text-blue-700"
          >
            Find us on Slack
          </a>
        </div>
      </div>
    </div>
  </div>
</div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
              <h2 className="text-2xl font-bold mb-6">Send us a message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 
                             focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 
                             focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company (Optional)
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 
                             focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 
                             focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium 
                           hover:bg-blue-700 transition-colors flex items-center justify-center"
                >
                  Send Message
                  <Send size={18} className="ml-2" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600">
              Can't find what you're looking for? Reach out to our team.
            </p>
          </div>

          <div className="space-y-6">
            {[
              {
                question: "How quickly can I expect a response?",
                answer: "We typically respond to all inquiries within 24 business hours."
              },
              {
                question: "Do you offer technical support?",
                answer: "Yes, we provide comprehensive technical support for all our customers through email and live chat."
              },
              {
                question: "Can I schedule a demo?",
                answer: "Absolutely! You can schedule a demo through our contact form or by emailing us directly."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-2">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;